/**
 * EditLeadDrawer.tsx — SB-2's lead edit, the first UI caller of the
 * long-standing PUT /v1/sales/contacts/{id} (the backend full-field update
 * has existed since 0032-era; no UI ever called it — the M7a lesson in
 * reverse). House drawer pattern (NewDealDrawer template): .k-drawer over a
 * scrim, portal, Escape closes.
 *
 * SET-ONLY, stated in the UI: the server's CASE-WHEN keep semantics treat
 * '' as "unchanged", so a field can be edited but not CLEARED from here.
 * The drawer therefore sends only non-empty values and says so — adopting
 * the OptionalString clear-to-null pattern is a separate, undecided slice.
 * Source is the SB-1 slug vocabulary; a legacy free-text source shows
 * verbatim as the current value until a slug is chosen (data honesty —
 * the select adds a transient option for it rather than mislabeling it).
 */
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import type { SalesLead } from '../../types'
import { updateLead } from '../../api'
import { apiErrorMessage } from '../../../../api/client'
import { toast } from 'sonner'
import { overlay } from '../../../../theme/tokens'
import { SOURCE_OPTIONS } from './vocab'
import { OwnerSelect } from './OwnerSelect'
import { AccountField } from './AccountField'
import type { UpdateContactRequest } from '../../../../api/types'

export function EditLeadDrawer({ lead, onClose, onSaved }: {
  lead: SalesLead
  onClose: () => void
  onSaved: (updated: SalesLead) => void
}) {
  const [name, setName]         = useState(lead.name)
  const [email, setEmail]       = useState(lead.email)
  const [phone, setPhone]       = useState(lead.phone)
  const [title, setTitle]       = useState(lead.title)
  // §25 L-1: the Company field is the AccountPicker (pick-only, clearable);
  // its text IS the company, the pick is the link.
  const [account, setAccount]   = useState({ accountId: lead.accountId ?? '', company: lead.company })
  const [location, setLocation] = useState(lead.location)
  const [source, setSource]     = useState(lead.source)
  const [notes, setNotes]       = useState(lead.notes)
  // Slice E / E-4: the owner LINK (employee id). A queue lead's owner drives
  // convert's inheritance, so this is the surface where it matters most. Sent
  // ONLY when changed — the changed-fields rule (E2.2).
  const [assignedEmployeeId, setAssignedEmployeeId] = useState(lead.assignedEmployeeId ?? '')
  const [saving, setSaving]     = useState(false)

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  const legacySource = lead.source !== '' && !SOURCE_OPTIONS.some(o => o.value === lead.source)

  async function handleSave() {
    if (!name.trim()) { toast.error('Name is required'); return }
    setSaving(true)
    try {
      const body: UpdateContactRequest = {}
      const put = (k: keyof Omit<UpdateContactRequest, 'accountId'>, v: string) => { if (v.trim() !== '') body[k] = v.trim() }
      put('fullName', name)
      put('email', email)
      put('phone', phone)
      put('title', title)
      put('company', account.company)
      // §25 L-2: the link rides ONLY when it changed — an id to link, an
      // explicit null to clear (the one field on this wire that can be).
      if (account.accountId !== (lead.accountId ?? '')) body.accountId = account.accountId === '' ? null : account.accountId
      put('location', location)
      // SB-6: send source ONLY when the user changed it. The stored value —
      // legacy free text included — pre-selects on load (the honesty rule),
      // but an untouched legacy value must not ride the wire: validateSource
      // would refuse it and poison the whole save. '' still means keep, so a
      // switch to '(keep current)' also sends nothing.
      if (source !== lead.source) put('source', source)
      put('notes', notes)
      // E-4: the owner rides the wire only when it actually changed.
      if (assignedEmployeeId !== (lead.assignedEmployeeId ?? '')) put('assignedEmployeeId', assignedEmployeeId)
      const updated = await updateLead(lead.id, body)
      toast.success('Lead updated')
      onSaved(updated)
      onClose()
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to update lead'))
    } finally {
      setSaving(false)
    }
  }

  const inputCls = "w-full px-4 py-3 bg-surface-control border border-edge-control rounded-control text-13 text-ink-body placeholder:text-ink-muted transition-all"
  const labelCls = "block text-12 font-semibold text-ink-body mb-2"

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: overlay.scrim, backdropFilter: 'blur(2px)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="k-drawer"
        style={{ width: 'min(520px, 100vw)', animation: 'slide-in-right 220ms cubic-bezier(0.23,1,0.32,1)' }}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 flex-shrink-0 border-b border-edge-row">
          <div>
            <div className="text-detail-title font-bold text-ink-primary">Edit lead</div>
            <div className="text-12 text-ink-muted mt-1">Blank fields keep their current value — clearing isn't supported yet</div>
          </div>
          <button onClick={onClose} aria-label="Close"
            className="w-8 h-8 flex items-center justify-center rounded-control text-ink-muted hover:bg-surface-nav-hover transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5">
          <div>
            <label className={labelCls}>Name *</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} autoFocus className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Phone</label>
              <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} className={inputCls} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls} htmlFor="lead-edit-company">Company</label>
              <AccountField id="lead-edit-company" value={account} onChange={setAccount} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Title</label>
              <input type="text" value={title} onChange={e => setTitle(e.target.value)} className={inputCls} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Owner</label>
            {/* E-4: the employee LINK (E360-2). Empty keeps the current owner —
                shown as the hint when the lead carries a legacy text owner. */}
            <OwnerSelect value={assignedEmployeeId} onChange={setAssignedEmployeeId}
              className={`${inputCls} bg-surface-card`}
              inheritHint={lead.assignedTo && !lead.assignedEmployeeId ? `Currently ${lead.assignedTo} (keep)` : undefined} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Location</label>
              <input type="text" value={location} onChange={e => setLocation(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Source</label>
              <select value={source} onChange={e => setSource(e.target.value)}
                className={`${inputCls} bg-surface-card`}>
                <option value="">{lead.source === '' ? 'No source' : '(keep current)'}</option>
                {legacySource && <option value={lead.source}>{lead.source}</option>}
                {SOURCE_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className={labelCls}>Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={4}
              className={`${inputCls} resize-none`} />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-6 pt-5 pb-14 border-t border-edge-row flex-shrink-0">
          <button onClick={onClose} className="k-btn-secondary">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="k-btn k-btn--lg disabled:opacity-60">
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
